import React, { useMemo, useState } from "react";

/**
 * Mythodex — player-facing chronicle index.
 * Read-only; the GM maintains it. Grouped by chapter where it adds signal.
 */
export default function Mythodex({ adventure, fresh }) {
  const my = adventure?.mythodex;
  const glow = (key) => (fresh?.has(key) ? "mythodex-fresh" : "");
  const chapter = adventure?.chapter || 1;
  const eventsByChapter = useMemo(() => groupByChapter(my?.events || []), [my?.events]);

  if (!my) {
    return (
      <div className="p-4 font-mono text-xs text-stone-500" data-testid="mythodex-empty">
        — the archive is empty —
      </div>
    );
  }

  const activeObjectives = (my.objectives || []).filter((o) => o.status === "active");
  const resolvedObjectives = (my.objectives || []).filter((o) => o.status !== "active");
  const openThreads = (my.threads || []).filter((t) => t.status === "open");
  const resolvedThreads = (my.threads || []).filter((t) => t.status !== "open");

  return (
    <div className="p-4 space-y-5 font-serif-body text-sm text-stone-200" data-testid="mythodex-panel">
      <div className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold flex items-center justify-between">
        <span>Mythodex</span>
        <span className="text-stone-500">Ch. {chapter}</span>
      </div>

      <Section title="Character" testid="mythodex-character">
        {my.character?.name ? (
          <div className={`space-y-1.5 ${glow("character")}`} data-fresh={fresh?.has("character") ? "true" : undefined}>
            <div className="text-stone-100 font-serif-display">{my.character.name}</div>
            {my.character.pronouns && (
              <div className="text-[0.7rem] font-mono uppercase tracking-widest text-stone-500">{my.character.pronouns}</div>
            )}
            {my.character.background && (
              <div className="text-stone-300 italic">{my.character.background}</div>
            )}
            {(my.character.notes || []).length > 0 && (
              <ul className="pt-1 space-y-1">
                {my.character.notes.map((n, i) => (
                  <li key={`${i}-${n.slice(0, 10)}`} className="border-l border-amber-500/30 pl-2 text-stone-300">{n}</li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <Empty>unnamed wayfarer</Empty>
        )}
      </Section>

      <Section title="Objectives" testid="mythodex-objectives">
        {activeObjectives.length === 0 ? (
          <Empty>no active goals</Empty>
        ) : (
          <ul className="space-y-1.5">
            {activeObjectives.map((o) => (
              <li key={o.id} className={`border-l-2 border-amber-500/60 pl-2.5 ${glow(`obj:${o.id}`)}`} data-fresh={fresh?.has(`obj:${o.id}`) ? "true" : undefined} data-testid={`mythodex-obj-${o.id}`}>
                <div className="text-stone-100">{o.text}</div>
                <div className="text-[0.6rem] font-mono uppercase tracking-widest text-stone-600">opened ch. {o.chapter_added}</div>
              </li>
            ))}
          </ul>
        )}
        {resolvedObjectives.length > 0 && (
          <Collapsible label={`${resolvedObjectives.length} resolved`}>
            <ul className="space-y-1 mt-1.5">
              {resolvedObjectives.map((o) => (
                <li key={o.id} className={`pl-2.5 opacity-60 ${glow(`obj:${o.id}`)}`}>
                  <span className="line-through text-stone-400">{o.text}</span>
                  <span className={`ml-2 text-[0.6rem] font-mono uppercase tracking-widest ${o.status === "failed" ? "text-red-400" : "text-emerald-400"}`}>
                    {o.status}
                  </span>
                </li>
              ))}
            </ul>
          </Collapsible>
        )}
      </Section>

      <Section title="Characters" testid="mythodex-characters">
        {(my.characters || []).length === 0 ? (
          <Empty>none met yet</Empty>
        ) : (
          <ul className="space-y-2.5">
            {my.characters.map((c) => (
              <li key={c.name} className={`border border-stone-800 bg-stone-900/40 rounded-sm p-2.5 ${glow(`char:${c.name}`)}`} data-fresh={fresh?.has(`char:${c.name}`) ? "true" : undefined} data-testid={`mythodex-char-${c.name.replace(/\s+/g, "-")}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-stone-100 font-serif-display">{c.name}</span>
                  <span className={`text-[0.6rem] font-mono uppercase tracking-widest ${statusColor(c.status)}`}>{c.status}</span>
                </div>
                <div className="text-[0.7rem] font-mono uppercase tracking-widest text-stone-500 mb-1">{c.role} · met ch. {c.chapter_introduced}</div>
                {c.notes && <div className="text-stone-300 text-[0.85rem] leading-snug">{c.notes}</div>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Plot Threads" testid="mythodex-threads">
        {openThreads.length === 0 && resolvedThreads.length === 0 ? (
          <Empty>no threads unwound</Empty>
        ) : (
          <>
            {openThreads.length > 0 && (
              <ul className="space-y-1.5">
                {openThreads.map((t) => (
                  <li key={t.id} className={`border-l-2 border-purple-500/60 pl-2.5 ${glow(`thr:${t.id}`)}`} data-fresh={fresh?.has(`thr:${t.id}`) ? "true" : undefined} data-testid={`mythodex-thr-${t.id}`}>
                    <div className="text-stone-100">{t.title}</div>
                    <div className="text-[0.6rem] font-mono uppercase tracking-widest text-stone-600">opened ch. {t.chapter_opened}</div>
                  </li>
                ))}
              </ul>
            )}
            {resolvedThreads.length > 0 && (
              <Collapsible label={`${resolvedThreads.length} resolved`}>
                <ul className="space-y-1 mt-1.5">
                  {resolvedThreads.map((t) => (
                    <li key={t.id} className={`pl-2.5 opacity-60 ${glow(`thr:${t.id}`)}`}>
                      <span className="line-through text-stone-400">{t.title}</span>
                      <span className="ml-2 text-[0.6rem] font-mono uppercase tracking-widest text-emerald-400">resolved</span>
                    </li>
                  ))}
                </ul>
              </Collapsible>
            )}
          </>
        )}
      </Section>

      <Section title="Chronicle" testid="mythodex-events">
        {Object.keys(eventsByChapter).length === 0 ? (
          <Empty>no events logged</Empty>
        ) : (
          <div className="space-y-3">
            {Object.entries(eventsByChapter).map(([ch, events]) => (
              <div key={ch}>
                <div className="font-mono text-[0.6rem] uppercase tracking-widest gold mb-1">Chapter {ch}</div>
                {my.recaps?.[ch] && (
                  <div className="mb-1.5 pl-2 italic text-stone-400 text-[0.8rem] leading-snug" data-testid={`mythodex-recap-${ch}`}>
                    {my.recaps[ch]}
                  </div>
                )}
                <ul className="space-y-1.5 pl-2 border-l border-amber-500/20">
                  {events.map(({ e, idx }) => (
                    <li
                      key={idx}
                      className={`pl-2 text-stone-300 text-[0.85rem] leading-snug ${glow(`evt:${idx}`)}`}
                      data-fresh={fresh?.has(`evt:${idx}`) ? "true" : undefined}
                      data-testid={`mythodex-evt-${idx}`}
                    >
                      {e.summary}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-mono text-[0.6rem] uppercase tracking-widest gold mb-2">{title}</div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="font-mono text-xs italic text-stone-600">— {children} —</div>;
}

function Collapsible({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        className="font-mono text-[0.6rem] uppercase tracking-widest text-stone-500 hover:text-amber-400 transition-colors"
      >
        {open ? "▾" : "▸"} {label}
      </button>
      {open && children}
    </div>
  );
}

function statusColor(status) {
  const s = (status || "").toLowerCase();
  if (["dead", "fallen", "slain", "killed"].some((k) => s.includes(k))) return "text-red-400";
  if (["wounded", "injured", "hurt", "betrayed"].some((k) => s.includes(k))) return "text-amber-400";
  if (["allied", "ally", "friend", "loyal"].some((k) => s.includes(k))) return "text-emerald-400";
  return "text-stone-400";
}

function groupByChapter(events) {
  const groups = {};
  events.forEach((e, idx) => {
    const key = String(e.chapter ?? "?");
    if (!groups[key]) groups[key] = [];
    groups[key].push({ e, idx });
  });
  return groups;
}
